/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import {
    object, shape, string, arrayOf,
} from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { useUIDSeed } from 'react-uid';
import LinkOrATag from '../../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import { getFeatureFlags, getCurrentBrandId } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getOperatingSystem } from '../../../../../state/ducks/App/ducks/Config/Config-Helpers';
import triggerPopChatEvent from '../../../MultiBrandChatController/AigoChatWidgetWrapperV2/popOpenChat';

const styles = (theme) => ({
    customerOptions: {
        maxWidth: '80%',
        margin: '30px auto;',
        justifyContent: 'center',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '100%',
            margin: '15px auto;',
            display: 'block',
        },
    },
    customerOptionGrid: {
        boxSizing: 'border-box',
        padding: '5px',
    },
    customerOption: {
        color: theme.palette.common?.black,
        border: `1px solid ${theme.palette.grey8}`,
        padding: '11px 15px',
        height: '100%',
        '& svg': {
            fill: theme.palette.cms?.primary || theme.palette.colorPrimary,
        },
        '& a': {
            color: theme.palette.contentColor,
            display: 'block',
            fontSize: '13px',
            textAlign: 'left',
            lineHeight: '2em',
            textDecoration: 'none',
        },
        '& .option': {
            display: 'block',
            fontSize: '13px',
            textAlign: 'left',
            lineHeight: '2em',
            cursor: 'pointer',
        },
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        fontWeight: 600,
        '& img': {
            width: 40,
            height: 35,
            marginRight: 7,
        },
    },
    subLink: {
        color: theme.palette.contentColor,
        display: 'block',
        fontSize: '13px',
        textAlign: 'left',
        lineHeight: '2em',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
});

const CustomerOptionsBlock = ({ data, classes }) => {
    const seed = useUIDSeed();
    const { options } = data;
    const operatingSystem = getOperatingSystem().toLowerCase();
    const currentBrand = useSelector((state) => getCurrentBrandId(state));
    const featureFlags = useSelector((state) => getFeatureFlags(state));
    const isIosChatEnabled = ((featureFlags['is-abc-in-pwa-enabled'] && operatingSystem === 'ios'));
    const customerData = useSelector((state) => state?.member);
    const isChatOption = (option) => option.link?.title && option.link.title.toLowerCase()?.includes('chat');
    const shouldRenderOption = (option) => !isChatOption(option) || (
        featureFlags['is-cs-page-chat-enabled']
        && (!['ios', 'android'].includes(operatingSystem) || isIosChatEnabled)
    );
    const visibleOptions = options.group?.filter(shouldRenderOption) || [];
    const gridColumnSize = visibleOptions.length === 5 ? 4 : 3;
    return (
        <>
            <Grid container className={classes.customerOptions}>
                {visibleOptions.length > 0
                && visibleOptions.map((d) => {
                    if (isChatOption(d)) {
                        return (
                            <React.Fragment key={seed(d)}>
                                <Grid item xs={12} sm={gridColumnSize} md={gridColumnSize} lg={gridColumnSize} className={classes.customerOptionGrid}>
                                    <div
                                        aria-hidden="true"
                                        className={classes.customerOption}
                                        onClick={() => triggerPopChatEvent({}, operatingSystem, currentBrand, featureFlags, customerData)}
                                    >
                                        <span className="option">
                                            <div className={classes.title}>
                                                {d?.icon_image?.url && (
                                                    <img src={d.icon_image.url} alt={d?.link?.title} />
                                                )}
                                                <span>{d?.link?.title}</span>
                                            </div>
                                            {d.sub_links && d.sub_links.length > 0 && d.sub_links.map((a) => (
                                                <span key={seed(a)} className={classes.subLink}>{a?.title}</span>
                                            ))}
                                        </span>
                                    </div>
                                </Grid>
                            </React.Fragment>
                        );
                    }
                    return (
                        <React.Fragment key={seed(d)}>
                            <Grid item xs={12} sm={gridColumnSize} md={gridColumnSize} lg={gridColumnSize} className={classes.customerOptionGrid}>
                                <div className={classes.customerOption}>
                                    <div className={classes.title}>
                                        {d?.icon_image?.url && (
                                            <img src={d.icon_image.url} alt={d?.link?.title || 'Account Section'} />
                                        )}
                                        <span>{d?.link?.title}</span>
                                    </div>
                                    {d.sub_links && d.sub_links.length > 0 && d.sub_links.map((a) => (
                                        <LinkOrATag key={seed(a)} className={classes.subLink} href={a?.href}>
                                            {a?.title}
                                        </LinkOrATag>
                                    ))}
                                </div>
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Grid>
        </>
    );
};

CustomerOptionsBlock.propTypes = {
    classes: object.isRequired,
    data: shape({
        options: shape({
            group: arrayOf(shape({
                link: shape({
                    title: string.isRequired,
                }).isRequired,
                icon: string.isRequired,
            })),
        }).isRequired,
    }).isRequired,
};
export default withStyles(styles)(CustomerOptionsBlock);
